import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { t } from '../../i18n/nl.js';
import { fmtDate } from '../../utils/formatUtils.js';
import { useToast } from '../../contexts/ToastContext';
import ConfirmModal from '../../components/ConfirmModal';
import { getActiveClientMemberships, membershipDisplayName } from '../../utils/membershipUtils.js';

async function downloadAgreementPDF(pdfPath) {
  const { data, error } = await supabase.storage
    .from('agreements')
    .createSignedUrl(pdfPath, 300);
  if (error || !data?.signedUrl) throw new Error(t('mijnAbonnement.downloadFout'));
  window.open(data.signedUrl, '_blank');
}

// Een client mag altijd opzeggen, ook tijdens de initiële looptijd —
// maar dan wordt de opzegging pas van kracht op de contract-einddatum
// (initial_end_date), zodat de automatische verlenging met 1 maand
// niet nog een keer plaatsvindt. Is het abonnement al rolling, dan
// geldt de bestaande opzegtermijn van 30 dagen vanaf de laatste
// creditboeking.
function computeCancellationDate(m) {
  if (!m.is_rolling && m.initial_end_date) return m.initial_end_date;
  const anchor = m.last_credit_booking_at
    ? new Date(m.last_credit_booking_at)
    : new Date(m.start_date);
  return new Date(anchor.getTime() + 30 * 86400000).toISOString().split('T')[0];
}

async function rejectAgreementViaEF(tokenStr, reason) {
  const res = await fetch(`${supabase.supabaseUrl}/functions/v1/reject-agreement`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: tokenStr, reason: reason || null }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Afwijzen mislukt');
}

export default function MijnAbonnementPage() {
  const { user } = useAuth();
  const { showError } = useToast();
  const [memberships, setMemberships] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [pendingTokens, setPendingTokens] = useState([]);
  const [allTokens, setAllTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rejectModal, setRejectModal] = useState(null); // { id, tokenStr }
  const [rejectReason, setRejectReason] = useState('');
  const [rejectBusy, setRejectBusy] = useState(false);
  const [cancelBusyId, setCancelBusyId] = useState(null);
  const [cancelModal, setCancelModal] = useState(null); // membership
  const [reactivateModal, setReactivateModal] = useState(null); // membership
  const [reactivateChecked, setReactivateChecked] = useState(false);
  const [reactivateBusy, setReactivateBusy] = useState(false);
  const [reactivateBusyId, setReactivateBusyId] = useState(null);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [membershipData, agreementsRes, pendingRes, allTokensRes] = await Promise.all([
        getActiveClientMemberships(user.id),
        supabase
          .from('signed_agreements')
          .select(`
            id,
            signed_at,
            pdf_url,
            membership_type:membership_types(name)
          `)
          .eq('client_id', user.id)
          .order('signed_at', { ascending: false }),
        // UITBREIDING 10: haal openstaande tokens op
        supabase
          .from('agreement_tokens')
          .select('id, token, created_at, expires_at, membership_type:membership_types(name)')
          .eq('client_id', user.id)
          .is('used_at', null)
          .is('rejected_at', null)
          .gt('expires_at', new Date().toISOString())
          .order('created_at', { ascending: false }),
        // BUG 18: alle tokens laden (ook afgewezen/verlopen/ingetrokken)
        supabase
          .from('agreement_tokens')
          .select('id, token, created_at, expires_at, used_at, rejected_at, rejection_reason, rejected_by, membership_type:membership_types(name)')
          .eq('client_id', user.id)
          .order('created_at', { ascending: false })
          .limit(20),
      ]);

      if (agreementsRes.error) throw agreementsRes.error;
      if (pendingRes.error) throw pendingRes.error;
      if (allTokensRes.error) throw allTokensRes.error;

      setMemberships(membershipData);
      setAgreements(agreementsRes.data || []);
      setPendingTokens(pendingRes.data || []);
      setAllTokens(allTokensRes.data || []);
    } catch (err) {
      setError(t('mijnAbonnement.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [user?.id]);

  async function handleReject() {
    if (!rejectModal) return;
    setRejectBusy(true);
    try {
      await rejectAgreementViaEF(rejectModal.tokenStr, rejectReason);
      setRejectModal(null);
      setRejectReason('');
      await load();
    } catch (err) {
      showError(t('mijnAbonnement.afwijzenFout') + err.message);
    } finally {
      setRejectBusy(false);
    }
  }

  function handleCancelMembership(membership) {
    if (!membership) return;
    setCancelModal(membership);
  }

  async function handleCancelConfirm() {
    const membership = cancelModal;
    if (!membership) return;
    const cancDate = computeCancellationDate(membership);

    setCancelModal(null);
    setCancelBusyId(membership.id);
    try {
      const { error: rpcErr } = await supabase.rpc('cancel_client_membership', {
        p_membership_id: membership.id,
        p_cancellation_date: cancDate,
      });
      if (rpcErr) throw rpcErr;

      try {
        await supabase.functions.invoke('send-cancellation-confirmation', {
          body: { client_id: user.id, cancellation_date: cancDate },
        });
      } catch (_) { /* notificaties niet-fataal */ }

      await load();
    } catch (err) {
      showError(t('mijnAbonnement.opzeggenFout') + err.message);
    } finally {
      setCancelBusyId(null);
    }
  }

  function handleReactivateMembership(membership) {
    if (!membership) return;
    setReactivateChecked(false);
    setReactivateModal(membership);
  }

  async function handleReactivateConfirm() {
    const membership = reactivateModal;
    if (!membership || !reactivateChecked) return;
    setReactivateBusy(true);
    setReactivateBusyId(membership.id);
    try {
      const { error: rpcErr } = await supabase.rpc('reactivate_membership', {
        p_membership_id: membership.id,
      });
      if (rpcErr) throw rpcErr;

      setReactivateModal(null);
      await load();
    } catch (err) {
      showError(t('mijnAbonnement.heractiverenFout') + err.message);
    } finally {
      setReactivateBusy(false);
      setReactivateBusyId(null);
    }
  }

  const todayStr = new Date().toISOString().slice(0, 10);
  const isMembershipActive = (m) => !m.end_date || m.end_date >= todayStr;
  const hasActiveMembership = memberships.some(isMembershipActive);
  const firstPendingToken = pendingTokens[0];
  const agreementUrl = firstPendingToken
    ? `${window.location.origin}/overeenkomst/${firstPendingToken.token}`
    : null;

  return (
    <div>
      {/* Afwijzen-dialoog (UITBREIDING 12) */}
      {rejectModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('mijnAbonnement.afwijzenTitel')}</h2>
              <button className="modal-close" onClick={() => setRejectModal(null)} disabled={rejectBusy}>✕</button>
            </div>
            <p style={{ marginBottom: 16, fontSize: 14, color: 'var(--color-text-muted)' }}>
              {t('mijnAbonnement.afwijzenUitleg')}
            </p>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">{t('mijnAbonnement.afwijzenRedenLabel')}</label>
              <textarea
                className="form-input"
                rows={3}
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder={t('mijnAbonnement.afwijzenRedenPlaceholder')}
                style={{ resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setRejectModal(null)} disabled={rejectBusy}>
                {t('mijnAbonnement.afwijzenAnnuleer')}
              </button>
              <button className="btn btn-danger" onClick={handleReject} disabled={rejectBusy}>
                {rejectBusy ? t('mijnAbonnement.afwijzenBezig') : t('mijnAbonnement.afwijzenBevestig')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Heractiveren-dialoog */}
      {reactivateModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('mijnAbonnement.heractiverenTitel')}</h2>
              <button className="modal-close" onClick={() => setReactivateModal(null)} disabled={reactivateBusy}>✕</button>
            </div>
            <p style={{ marginBottom: 16, fontSize: 14, color: 'var(--color-text-muted)' }}>
              {t('mijnAbonnement.heractiverenUitleg')}
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={reactivateChecked}
                onChange={e => setReactivateChecked(e.target.checked)}
              />
              {t('mijnAbonnement.heractiverenAkkoord')}
            </label>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setReactivateModal(null)} disabled={reactivateBusy}>
                {t('mijnAbonnement.afwijzenAnnuleer')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleReactivateConfirm}
                disabled={!reactivateChecked || reactivateBusy}
              >
                {reactivateBusy ? '...' : t('mijnAbonnement.heractiverenBevestigKnop')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">{t('mijnAbonnement.title')}</h1>
        </div>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <>
          {/* Sectie 1: Actieve abonnementen (gestapelde kaarten bij meerdere) */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
              {t('mijnAbonnement.actiefAbonnement')}
            </h2>

            {memberships.length === 0 ? (
              <p className="text-muted">{t('mijnAbonnement.geenAbonnement')}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {memberships.map((membership, idx) => {
                  const isActive = isMembershipActive(membership);
                  const displayName = membershipDisplayName(membership, t('mijnAbonnement.onbekend'));
                  const isCancelBusy = cancelBusyId === membership.id;
                  const isReactivateBusy = reactivateBusyId === membership.id;
                  const canReactivate = !!membership.cancellation_date && membership.cancellation_date >= todayStr;
                  return (
                    <div
                      key={membership.id}
                      style={idx > 0 ? { paddingTop: 24, borderTop: '1px solid var(--color-border)' } : undefined}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className="text-muted">{t('mijnAbonnement.naam')}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <strong>{displayName}</strong>
                            {membership.is_custom && (
                              <span style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: 6,
                                fontSize: 11,
                                fontWeight: 600,
                                background: 'var(--color-primary-light)',
                                color: 'var(--color-primary)',
                              }}>
                                {t('mijnAbonnement.aangepast')}
                              </span>
                            )}
                          </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className="text-muted">{t('mijnAbonnement.looptijd')}</span>
                          <span>
                            {fmtDate(membership.start_date)} - {membership.end_date ? fmtDate(membership.end_date) : t('mijnAbonnement.doorlopend')}
                          </span>
                        </div>

                        {(membership.current_pt_credits > 0 || membership.pt_credits_per_month > 0) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span className="text-muted">{t('mijnAbonnement.ptCredits')}</span>
                            <span>
                              <strong style={{ fontSize: 16, color: 'var(--color-primary)' }}>{membership.current_pt_credits || 0}</strong>
                              <span className="text-muted" style={{ fontSize: 12, marginLeft: 4 }}>({membership.pt_credits_per_month || 0}/mnd)</span>
                            </span>
                          </div>
                        )}
                        {(membership.current_pt_plus_credits > 0 || membership.pt_plus_credits_per_month > 0) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span className="text-muted">{t('mijnAbonnement.ptPlusCredits')}</span>
                            <span>
                              <strong style={{ fontSize: 16, color: 'var(--color-primary)' }}>{membership.current_pt_plus_credits || 0}</strong>
                              <span className="text-muted" style={{ fontSize: 12, marginLeft: 4 }}>({membership.pt_plus_credits_per_month || 0}/mnd)</span>
                            </span>
                          </div>
                        )}
                        {(membership.current_small_group_credits > 0 || membership.small_group_credits_per_month > 0) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span className="text-muted">{t('mijnAbonnement.smallGroupCredits')}</span>
                            <span>
                              <strong style={{ fontSize: 16, color: 'var(--color-primary)' }}>{membership.current_small_group_credits || 0}</strong>
                              <span className="text-muted" style={{ fontSize: 12, marginLeft: 4 }}>({membership.small_group_credits_per_month || 0}/mnd)</span>
                            </span>
                          </div>
                        )}
                        {!membership.current_pt_credits && !membership.pt_credits_per_month &&
                         !membership.current_pt_plus_credits && !membership.pt_plus_credits_per_month &&
                         !membership.current_small_group_credits && !membership.small_group_credits_per_month && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span className="text-muted">{t('mijnAbonnement.resteCredits')}</span>
                            <strong style={{ fontSize: 16, color: 'var(--color-primary)' }}>
                              {membership.current_credits}
                            </strong>
                          </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className="text-muted">{t('mijnAbonnement.status')}</span>
                          <span style={{
                            fontWeight: 600,
                            color: isActive ? '#2e7d32' : 'var(--color-danger)',
                          }}>
                            {isActive ? t('mijnAbonnement.actief') : t('mijnAbonnement.verlopen')}
                          </span>
                        </div>

                        {/* Opzegging / heractivering */}
                        {membership.cancellation_date ? (
                          <>
                            <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: '#fef3c7', border: '1px solid #fcd34d' }}>
                              <span style={{ fontWeight: 600, color: '#92400e' }}>
                                {t('mijnAbonnement.opgezegd', { datum: fmtDate(membership.cancellation_date) })}
                              </span>
                            </div>
                            {canReactivate ? (
                              <div style={{ marginTop: 4 }}>
                                <button
                                  className="btn btn-primary"
                                  onClick={() => handleReactivateMembership(membership)}
                                  disabled={isReactivateBusy}
                                >
                                  {isReactivateBusy ? '...' : t('mijnAbonnement.heractiverenKnop')}
                                </button>
                              </div>
                            ) : (
                              <p style={{ marginTop: 4, fontSize: 12, fontStyle: 'italic', color: 'var(--color-text-muted)' }}>
                                {t('mijnAbonnement.heractiverenNietMeerMogelijk')}
                              </p>
                            )}
                          </>
                        ) : (isActive && (
                          <div style={{ marginTop: 4 }}>
                            <button
                              className="btn btn-danger"
                              onClick={() => handleCancelMembership(membership)}
                              disabled={isCancelBusy}
                            >
                              {isCancelBusy ? '...' : t('mijnAbonnement.opzeggenKnop')}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sectie 2: Overeenkomsten (openstaand + ondertekend) */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
              {t('mijnAbonnement.overeenkomsten')}
            </h2>

            {/* UITBREIDING 10: Openstaande tokens */}
            {pendingTokens.map(pt => (
              <div
                key={pt.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  marginBottom: 12,
                  borderRadius: 8,
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#1d4ed8', marginBottom: 4 }}>
                    📋 {t('mijnAbonnement.statusNietOndertekend')}
                  </div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    {t('mijnAbonnement.verstuurdOp')} {fmtDate(pt.created_at)}
                    {' · '}
                    {t('mijnAbonnement.geldigTm')} {fmtDate(pt.expires_at)}
                  </div>
                  {pt.membership_type?.name && (
                    <div style={{ fontSize: 13, marginTop: 2 }}>
                      {pt.membership_type.name}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <a
                    href={`/overeenkomst/${pt.token}`}
                    className="btn btn-primary btn-sm"
                  >
                    {t('mijnAbonnement.ondertekenenBtn')}
                  </a>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ color: 'var(--color-danger)' }}
                    onClick={() => { setRejectModal({ id: pt.id, tokenStr: pt.token }); setRejectReason(''); }}
                  >
                    {t('mijnAbonnement.afwijzenKnop')}
                  </button>
                </div>
              </div>
            ))}

            {/* BUG 18: Afgewezen / ingetrokken / verlopen tokens */}
            {allTokens.filter(tk => {
              const now = new Date();
              return (!!tk.rejected_at) || (!tk.used_at && !tk.rejected_at && new Date(tk.expires_at) < now);
            }).map(tk => {
              const isRejectedByClient = !!tk.rejected_at && tk.rejected_by === user.id;
              const isRevoked = !!tk.rejected_at && tk.rejected_by !== user.id;
              const isExpired = !tk.used_at && !tk.rejected_at && new Date(tk.expires_at) < new Date();
              const label = isRevoked ? t('mijnAbonnement.statusIngetrokken')
                : isRejectedByClient ? t('mijnAbonnement.statusAfgewezen')
                : t('mijnAbonnement.statusVerlopen');
              const color = isRevoked ? '#6b7280' : '#dc2626';
              return (
                <div key={tk.id} style={{ padding: '10px 14px', marginBottom: 8, borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color, marginBottom: 2 }}>{label}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {t('mijnAbonnement.verstuurdOp')} {fmtDate(tk.created_at)}
                    {tk.membership_type?.name && ` · ${tk.membership_type.name}`}
                  </div>
                  {tk.rejection_reason && (
                    <div style={{ fontSize: 12, marginTop: 4, color: 'var(--color-text-muted)' }}>
                      {t('mijnAbonnement.afwijzenRedenLabel')}: {tk.rejection_reason}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Ondertekende overeenkomsten */}
            {agreements.length === 0 && pendingTokens.length === 0 && allTokens.filter(tk => !!tk.rejected_at || (!tk.used_at && new Date(tk.expires_at) < new Date())).length === 0 ? (
              <p className="text-muted">{t('mijnAbonnement.geenOvereenkomsten')}</p>
            ) : agreements.length === 0 ? (
              <p className="text-muted" style={{ fontSize: 13 }}>{t('mijnAbonnement.geenOndertekend')}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {agreements.map(agr => (
                  <div
                    key={agr.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderTop: '1px solid var(--color-border)',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div className="text-muted" style={{ fontSize: 13, marginBottom: 4 }}>
                        ✅ {t('mijnAbonnement.ondertekendOp')} {fmtDate(agr.signed_at)}
                      </div>
                      <div style={{ fontWeight: 600 }}>
                        {agr.membership_type?.name || t('mijnAbonnement.onbekend')}
                      </div>
                    </div>
                    {/* BUG 5 fix: gebruik signed URL ipv directe storage path */}
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      onClick={async () => { try { await downloadAgreementPDF(agr.pdf_url); } catch (err) { showError(err.message); } }}
                    >
                      <span>↓</span>
                      <span>{t('mijnAbonnement.download')}</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sectie 3: Abonnement afsluiten */}
          {!hasActiveMembership && (
            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
                {t('mijnAbonnement.afsluitenTitel')}
              </h2>

              {agreementUrl ? (
                <>
                  <p className="text-muted" style={{ marginBottom: 16, lineHeight: 1.5 }}>
                    {t('mijnAbonnement.afsluitenInfo')}
                  </p>
                  <a
                    href={agreementUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                  >
                    <span>📄</span>
                    <span>{t('mijnAbonnement.afsluitenBtn')}</span>
                  </a>
                </>
              ) : (
                <p className="text-muted">{t('mijnAbonnement.contactCoach')}</p>
              )}
            </div>
          )}
        </>
      )}

      {cancelModal && (
        <ConfirmModal
          title={t('mijnAbonnement.opzeggenKnop')}
          body={t('mijnAbonnement.opzeggenBevestiging', { datum: fmtDate(computeCancellationDate(cancelModal)) })}
          onConfirm={handleCancelConfirm}
          onClose={() => setCancelModal(null)}
          danger
        />
      )}
    </div>
  );
}
